import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Compass, HeartHandshake, Route, Sparkles } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Topo, GridNetwork } from "@/components/Graphics";
import { PillLink, Kicker, CTABand } from "@/components/Bits";

const EASE = [0.22, 1, 0.36, 1];

const features = [
  { icon: Compass, title: "Simple, straightforward guidance", body: "Clear next steps without the jargon or the runaround." },
  { icon: Route, title: "Solutions built around your goals", body: "Your situation is unique. Your plan should be too." },
  { icon: HeartHandshake, title: "Friendly, pressure-free approach", body: "A real conversation — never a hard sell." },
  { icon: Sparkles, title: "A focus on understanding your options", body: "Know what's possible before you decide anything." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const topoY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const topoR = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[94vh] flex items-end overflow-hidden pt-32 pb-20">
      <motion.div
        style={{ y: topoY, rotate: topoR }}
        className="pointer-events-none absolute -right-[22%] top-[6%] w-[80vw] max-w-[900px] text-primary/25"
      >
        <Topo className="w-full h-full" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <Kicker>Mobile Home Solutions · South Carolina</Kicker>
        </motion.div>

        <h1 className="font-display tracking-mega leading-[0.82] text-[3.4rem] sm:text-8xl lg:text-[9.5rem] mt-6">
          <MaskedLines lines={["There's FaeVor", "in Your Future."]} />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-10 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-end"
        >
          <p className="text-lg sm:text-xl leading-relaxed text-foreground/75 max-w-xl">
            We help mobile and manufactured homeowners across South Carolina sell their homes — even homes
            that need work, sit in a park, or need to be moved. No pressure. No complicated process. Just real
            solutions designed to help you move forward.
          </p>
          <div className="flex flex-wrap gap-3">
            <PillLink to="/real-estate" variant="solid" testid="hero-cta-real-estate">
              Explore Real Estate
            </PillLink>
            <PillLink to="/contact" variant="outline" testid="hero-cta-talk">
              Talk With FaeVor
            </PillLink>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function MarqueeBand() {
  return (
    <div className="border-y border-border bg-secondary/60 py-6">
      <Marquee speed={34} gradient={false} autoFill>
        {["Real Estate Solutions", "There's FaeVor in Your Future", "Sell With Confidence", "Mobile & Manufactured Homes"].map(
          (t, i) => (
            <span key={i} className="flex items-center">
              <span className="font-display text-3xl sm:text-4xl tracking-mega mx-8 text-foreground/85">{t}</span>
              <span className="text-primary text-3xl">✳</span>
            </span>
          )
        )}
      </Marquee>
    </div>
  );
}

function Pathways() {
  const cards = [
    {
      to: "/real-estate",
      num: "01",
      label: "Mobile Home Solutions",
      title: "Ready to sell your mobile home?",
      body: "Simple, stress-free options — in a park, on private land, or needing work.",
      Graphic: Topo,
      testid: "pathway-real-estate",
    },
    {
      to: "/about",
      num: "02",
      label: "About FaeVor Works",
      title: "Meet Shalanda Burgess.",
      body: "The story — and the person — behind FaeVor Works.",
      Graphic: GridNetwork,
      testid: "pathway-about",
    },
  ];
  return (
    <section data-testid="pathways" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
      <Reveal>
        <Kicker>How We Help</Kicker>
        <h2 className="font-display text-4xl sm:text-6xl tracking-mega leading-[0.9] mt-4 max-w-3xl">
          A clear path to your next move.
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {cards.map((c, i) => (
          <Reveal key={c.num} delay={i * 0.1}>
            <Link
              to={c.to}
              data-testid={c.testid}
              className="group relative flex min-h-[420px] flex-col justify-between overflow-hidden rounded-3xl border border-border bg-card p-8 sm:p-10 transition-colors duration-500 hover:bg-accent hover:text-accent-foreground"
            >
              <div className="pointer-events-none absolute -right-16 -top-10 w-[60%] text-foreground/10 transition-transform duration-700 group-hover:scale-110 group-hover:text-accent-foreground/15">
                <c.Graphic className="w-full h-full" />
              </div>
              <div className="relative flex items-center justify-between">
                <span className="font-display text-sm text-primary">{c.num}</span>
                <span className="text-xs uppercase tracking-[0.22em] opacity-60">{c.label}</span>
              </div>
              <div className="relative">
                <h3 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1] max-w-md">
                  {c.title}
                </h3>
                <p className="mt-4 text-lg opacity-70 max-w-sm">{c.body}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:text-primary-foreground transition-colors">
                  Explore <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Why() {
  return (
    <section data-testid="why-faevor" className="bg-secondary/50 border-y border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.3fr] lg:gap-20">
          <Reveal>
            <Kicker>Why FaeVor Works?</Kicker>
            <h2 className="font-display text-4xl sm:text-5xl tracking-mega leading-[0.95] mt-5">
              Your goals may differ. The need is the same.
            </h2>
            <p className="mt-6 text-lg text-foreground/70 max-w-md">
              The right solution can create a better opportunity. FaeVor Works makes selling your mobile or
              manufactured home simpler — so you can move forward with clarity and confidence.
            </p>
          </Reveal>

          <div className="grid gap-5 sm:grid-cols-2">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-border bg-background p-7 transition-transform duration-500 hover:-translate-y-1">
                  <f.icon className="text-primary" size={28} strokeWidth={1.6} />
                  <h3 className="font-display text-xl tracking-tight mt-6">{f.title}</h3>
                  <p className="mt-2 text-foreground/65 leading-relaxed">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <MarqueeBand />
      <Pathways />
      <Why />
      <CTABand />
    </div>
  );
}
